import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import LoadingSpinner from "../../components/LoadingSpinner";
import { API_ciudadanos, API_tecnicos } from "../../config/apiConfig";
import "./css/Notificaciones.css";
import { API_BASE_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";
import { confirmToast } from "../../components/toast/confirmToast";

/* ---------- helper para cambiar estado ---------- */
const postEstadoUsuario = (userId, estado, motivo = "") =>
  fetch(`${API_ciudadanos}/${userId}/cambiarEstado`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      estadoUsuario: estado,
      motivoRechazo: motivo, // vacío cuando se acepta
    }),
  });

const Notificaciones = () => {
  const { user, role } = useSelector((s) => s.auth) || {};
  const { token } = useAuth();

  const [pendientes, setPendientes] = useState([]);
  const [citasPendientes, setCitasPend] = useState([]);
  const [citasCancel, setCitasCancel] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [rechazoActivo, setRechazoActivo] = useState(null); // userId en modo rechazo
  const [motivoRechazo, setMotivoRechazo] = useState("");

  // Estado del modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalTipo, setModalTipo] = useState(""); // "aceptar" | "agendar"
  const [modalData, setModalData] = useState(null); // usuario o cita
  const [fechaCita, setFechaCita] = useState("");
  const [horaCita, setHoraCita] = useState("");
  const [horaFinCita, setHoraFinCita] = useState("");
  const [mensajeRespuesta, setMensajeRespuesta] = useState("");
  const [tecnicoSeleccionado, setTecnicoSeleccionado] = useState("");
  const [modalidad, setModalidad] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  // carga inicial usuarios/citas/tecnicos
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(API_ciudadanos, {
          headers: {
            "x-auth-token": token,
          },
        });
        const ciudadanos = await res.json();

        /* usuarios Pendiente */
        setPendientes(ciudadanos.filter((u) => u.estadoUsuario === "Pendiente"));

        /* citas Pendiente */
        const todas = [];
        for (const c of ciudadanos) {
          const rc = await fetch(`${API_ciudadanos}/${c.userId}/citas`);
          const citas = await rc.json();
          citas
            .filter((ci) => ci.state === "Pendiente")
            .forEach((ci) => {
              // Asegurar que el appointmentId esté disponible para compatibilidad
              const citaConId = { ...c, ...ci, appointmentId: ci.id || ci._id };
              todas.push(citaConId);
            });
        }

        setCitasPend(todas);

        /* cargar tecnicos */
        const resTec = await fetch(API_tecnicos);
        const tecnicosData = await resTec.json();
        setTecnicos(tecnicosData);
      } catch (error) {
        console.error("Error al cargar notificaciones:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isRejecting]);

  // Abrir modal para aceptar usuario
  const abrirModalAceptar = (usuario) => {
    setModalTipo("aceptar");
    setModalData(usuario);
    setMensajeRespuesta("");
    setModalAbierto(true);
  };

  // Abrir modal para agendar cita
  const abrirModalAgendar = (cita) => {
    setModalTipo("agendar");
    setModalData(cita);

    // Prellenar campos con valores por defecto
    // Para citas pendientes, appointmentDate es null, así que usamos fecha de mañana
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const fechaDefecto = tomorrow.toISOString().split("T")[0];

    // Horas por defecto (9:00 AM a 10:00 AM)
    const horaInicioDefecto = "09:00";
    const horaFinDefecto = "10:00";

    setFechaCita(fechaDefecto);
    setHoraCita(horaInicioDefecto);
    setHoraFinCita(horaFinDefecto);
    setMensajeRespuesta("");
    setModalidad("Presencial"); // Valor por defecto

    // Buscar el técnico actual por nombre y role para preseleccionarlo
    const yoTec = tecnicos.find((t) => t.name === user?.name && t.role === role);
    setTecnicoSeleccionado(yoTec?.id || (tecnicos.length > 0 ? tecnicos[0].id : ""));
    setModalAbierto(true);
  };

  // Cerrar modal
  const cerrarModal = () => {
    setModalAbierto(false);
    setModalTipo("");
    setModalData(null);
    setFechaCita("");
    setHoraCita("");
    setHoraFinCita("");
    setMensajeRespuesta("");
    setTecnicoSeleccionado("");
    setModalidad("");
  };

  // Confirmar acción del modal
  const confirmarModal = async () => {
    if (modalTipo === "aceptar") {
      await aceptarUsuario(modalData);
    } else if (modalTipo === "agendar") {
      await asignarCita(modalData);
    }
    cerrarModal();
  };

  // aceptar
  const aceptarUsuario = async (u) => {
    try {
      await postEstadoUsuario(u.userId, "Aceptado");
      setPendientes((prev) => prev.filter((x) => x.userId !== u.userId));
      toast.success("Usuario aceptado. Contacta con él para activar el perfil.");
    } catch {
      toast.error("No se pudo aceptar al usuario");
    }
  };

  // rechazar
  const rechazarUsuario = async (u) => {
    if (!motivoRechazo.trim()) {
      toast.warn("Escribe el motivo del rechazo");
      return;
    }
    try {
      await postEstadoUsuario(u.userId, "Rechazado", motivoRechazo.trim());
      setPendientes((prev) => prev.filter((x) => x.userId !== u.userId));
      setRechazoActivo(null);
      setMotivoRechazo("");
      toast.error("Usuario rechazado");
    } catch {
      toast.error("No se pudo rechazar");
    }
  };

  // asignar cita (actualizado para usar el endpoint correcto)
  const asignarCita = async (cita) => {
    try {
      if (!fechaCita || !horaCita || !horaFinCita || !mensajeRespuesta || !tecnicoSeleccionado || !modalidad) {
        toast.warn("Por favor, completa todos los campos");
        return;
      }

      // Verificar que tenemos el appointmentId
      if (!cita?.appointmentId) {
        toast.error("Error: ID de cita no encontrado");
        return;
      }

      // Crear fecha completa con hora de inicio
      const appointmentDate = new Date(`${fechaCita}T${horaCita}`);

      // Usar endpoint /aceptar para agendar directamente la cita
      const response = await fetch(`${API_BASE_URL}/citas/${cita.appointmentId}/aceptar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appointmentDate: appointmentDate.toISOString(),
          responseMessage: mensajeRespuesta,
          technicianId: tecnicoSeleccionado,
          modality: modalidad,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      // Solo remover la cita específica del estado local si la API fue exitosa
      setCitasPend((cs) => cs.filter((c) => c.appointmentId !== cita.appointmentId));

      toast.success("Cita agendada correctamente.");
    } catch (error) {
      console.error("Error al agendar la cita:", error);
      toast.error(`Error al agendar la cita: ${error.message}`);
    }
  };

  // recahzar cita
  const rejectAppointment = async (appointmentId) => {
    try {
      setIsRejecting(true);
      const ok = await confirmToast({
        title: "¿Seguro que quiere rechazar la cita?",
        message: "Esta acción no se puede deshacer. La cita será cancelada.",
        confirmText: "Sí, rechazar",
        cancelText: "No, volver",
      });

      if (!ok) return;

      if (!appointmentId) {
        toast.error("Error: ID de cita no encontrado");
        return;
      }

      // Usar endpoint /aceptar para agendar directamente la cita
      const response = await fetch(`${API_BASE_URL}/citas/${appointmentId}/rechazar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Error deleting Appointment");
      }

      toast.success("Cita eliminada correctamente.");
    } catch (error) {
      console.error("Error al agendar la cita:", error);
      toast.error(`Error al agendar la cita: ${error.message}`);
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className="notificaciones">
      <Navbar />

      <main className="flex gap-50">
        <div className="solicitudes-cita mt-20">
          <h1 className="mayusculas font-18 mb-20 pl-20">Solicitud de cita</h1>
          {/*  citas pendientes  */}
          {loading ? (
            <LoadingSpinner message="Cargando solicitudes de cita..." />
          ) : (
            citasPendientes
              .filter((c) => !citasCancel.includes(c.appointmentId))
              .map((c) => (
                <div key={c.appointmentId} className="tarjeta-pendiente w-100">
                  <div className="column mb-20">
                    <p>
                      <strong>Servicio:</strong> {c.service}
                    </p>
                    <p>
                      <strong>Asunto:</strong> {c.subject}
                    </p>
                    {/* Mostrar servicios sociales si existen */}
                    {c.serviciosSociales && (
                      <div className="mt-5">
                        <p>
                          <strong>Miembros de la Familia:</strong> {c.serviciosSociales.nMiembrosFamilia}
                        </p>
                        <p>
                          <strong>Menores a cargo:</strong> {c.serviciosSociales.menoresACargo ? "Sí" : "No"}
                        </p>
                        <p>
                          <strong>Situación Social:</strong> {c.serviciosSociales.situacionSocial}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-10 mt-15">
                      <p className="w-200">
                        <strong>DNI:</strong> {c.dni}
                      </p>
                      <p>
                        <strong>Nombre:</strong> {c.name} {c.lastName}
                      </p>
                    </div>
                    <div className="flex gap-10">
                      <p className="w-200">
                        <strong>Teléfono:</strong> {c.phone}
                      </p>
                      <p>
                        <strong>Email:</strong> {c.email || "No disponible"}
                      </p>
                    </div>
                  </div>

                  <div className="flex end gap-10">
                    <button disabled={isRejecting} className="boton rojo" onClick={() => rejectAppointment(c?.appointmentId)}>
                      {isRejecting ? "Anulando Cita..." : "Anular la cita"}
                    </button>
                    <button className="boton verde" onClick={() => abrirModalAgendar(c)}>
                      Agendo yo la cita
                    </button>

                    {/* <button className="boton fondo-blanco" onClick={() => setCitasCancel((prev)=>[...prev,c.appointmentId])}>Cancelar</button> */}
                  </div>
                </div>
              ))
          )}
        </div>

        <div className="registros-pendientes mt-20">
          <h2 className="mayusculas flex align-baseline gap-[0.5rem] font-18 mb-50 pl-20">
            {Array.isArray(pendientes) && pendientes.length > 0 ? (
              <>
                Tienes <span className=" font-semibold text-base">({pendientes.length})</span>{" "}
                {pendientes.length === 1 ? "registro" : "registros"} por revisar
              </>
            ) : (
              "No hay registros por revisar"
            )}
          </h2>
          {/*  usuarios pendientes  */}
          {loading ? (
            <LoadingSpinner message="Cargando registros pendientes..." />
          ) : (
            pendientes.map((u) => (
              <div key={u.userId} className="tarjeta-pendiente w-100">
                <div className="column gap-5 mb-20">
                  <div className="flex gap-20">
                    <p className="w-175">
                      <strong>DNI:</strong> {u.dni}
                    </p>
                    <p>
                      <strong>Nombre:</strong> {u.name} {u.lastName}
                    </p>
                  </div>
                  <div className="flex gap-20">
                    <p className="w-175">
                      <strong>Teléfono:</strong> {u.phone}
                    </p>
                    <p>
                      <strong>Email:</strong> {u.email || "No disponible"}
                    </p>
                  </div>
                  <p>
                    <strong>Dirección:</strong> {u.address}, {u.city}, {u.postalCode}, {u.country}
                  </p>
                  <p>
                    <strong>Necesita:</strong> Ayuda con empleo/formación y Ayuda social
                  </p>
                </div>

                {rechazoActivo === u.userId ? (
                  <div className="column gap-10">
                    <textarea
                      rows={3}
                      placeholder="Motivo del rechazo…"
                      value={motivoRechazo}
                      onChange={(e) => setMotivoRechazo(e.target.value)}
                    />
                    <div className="flex end gap-10">
                      <button
                        className="boton fondo-blanco"
                        onClick={() => {
                          setRechazoActivo(null);
                          setMotivoRechazo("");
                        }}
                      >
                        Cancelar
                      </button>
                      <button className="boton rojo" onClick={() => rechazarUsuario(u)}>
                        Confirmar rechazo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex end gap-10">
                    <button className="boton rojo" onClick={() => setRechazoActivo(u.userId)}>
                      Rechazar
                    </button>
                    <button className="boton verde" onClick={() => aceptarUsuario(u)}>
                      Aceptar
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* Modal para aceptar usuario o agendar cita */}
      {modalAbierto && (
        <div className="modal">
          <div className="modal-content">
            <h3>{modalTipo === "aceptar" ? "Aceptar Usuario" : "Agendar Cita"}</h3>

            {modalTipo === "agendar" && (
              <>
                <div className="campo">
                  <label>Fecha de la cita:</label>
                  <input
                    type="date"
                    value={fechaCita}
                    onChange={(e) => setFechaCita(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>

                <div className="flex gap-30">
                  <div className="campo" style={{ flex: 1 }}>
                    <label>Hora de inicio:</label>
                    <input type="time" value={horaCita} onChange={(e) => setHoraCita(e.target.value)} />
                  </div>

                  <div className="campo" style={{ flex: 1 }}>
                    <label>Hora de fin:</label>
                    <input type="time" value={horaFinCita} onChange={(e) => setHoraFinCita(e.target.value)} />
                  </div>
                </div>

                <div className="campo">
                  <label>Técnico asignado:</label>
                  <select value={tecnicoSeleccionado} onChange={(e) => setTecnicoSeleccionado(e.target.value)}>
                    <option value="">Seleccionar técnico</option>
                    {tecnicos.map((tecnico) => (
                      <option key={tecnico.id} value={tecnico.id}>
                        {tecnico.name} - {tecnico.role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="campo">
                  <label>Modalidad:</label>
                  <select value={modalidad} onChange={(e) => setModalidad(e.target.value)}>
                    <option value="">Seleccionar modalidad</option>
                    <option value="Presencial">Presencial</option>
                    <option value="Virtual">Virtual</option>
                    <option value="Telefónica">Telefónica</option>
                  </select>
                </div>
              </>
            )}

            <div className="campo">
              <label>Mensaje de respuesta:</label>
              <textarea
                rows={4}
                placeholder="Escribe un mensaje de respuesta..."
                value={mensajeRespuesta}
                onChange={(e) => setMensajeRespuesta(e.target.value)}
              />
            </div>

            <div className="botones">
              <button className="boton" onClick={cerrarModal}>
                Cancelar
              </button>
              <button className="boton verde" onClick={confirmarModal}>
                {modalTipo === "aceptar" ? "Aceptar" : "Agendar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notificaciones;
